import React, { useCallback, useLayoutEffect, useMemo, useRef } from 'react';
import { useFrame } from '@react-three/fiber';
import { Text } from '@react-three/drei';
import * as THREE from 'three';

const DISABLE_RAYCAST = () => null;

// ---------------------------------------------------------
// PERFECT SEAMLESS FIBER DUCT
// ---------------------------------------------------------
class ArcCurve3 extends THREE.Curve {
  constructor(cx, cz, radius, startAngle, endAngle) {
    super();
    this.cx = cx;
    this.cz = cz;
    this.radius = radius;
    this.startAngle = startAngle;
    this.endAngle = endAngle;
  }

  getPoint(t, optionalTarget = new THREE.Vector3()) {
    const angle = this.startAngle + (this.endAngle - this.startAngle) * t;
    optionalTarget.set(
      this.cx + Math.cos(angle) * this.radius,
      0,
      this.cz + Math.sin(angle) * this.radius
    );
    return optionalTarget;
  }
}

const FiberDuct = ({
  positionX,
  centerZ,
  lengthZ,
  footprintWidthX,
  crossWidth,
  crossHeight,
  detourZ,
  detourDepth = 0,
  detourOuterHalfSpan = 4.4,
  detourInnerHalfSpan = 2.8
}) => {
  const sideX = footprintWidthX / 2 - crossWidth / 2;
  const sideZ = lengthZ / 2 - crossWidth / 2;
  const cornerRadius = crossWidth / 2;

  const geometry = useMemo(() => {
    const crossSection = new THREE.Shape();
    const cr = crossHeight / 2;
    const cx = Math.max(crossWidth / 2 - cr, 0.01);

    crossSection.moveTo(-cx, -cr);
    crossSection.lineTo(cx, -cr);
    crossSection.absarc(cx, 0, cr, -Math.PI / 2, Math.PI / 2, false);
    crossSection.lineTo(-cx, cr);
    crossSection.absarc(-cx, 0, cr, Math.PI / 2, -Math.PI / 2, false);

    const curvePath = new THREE.CurvePath();
    curvePath.add(new THREE.LineCurve3(
      new THREE.Vector3(-sideX + cornerRadius, 0, -sideZ),
      new THREE.Vector3(sideX - cornerRadius, 0, -sideZ)
    ));
    curvePath.add(new ArcCurve3(sideX - cornerRadius, -sideZ + cornerRadius, cornerRadius, -Math.PI / 2, 0));
    curvePath.add(new THREE.LineCurve3(
      new THREE.Vector3(sideX, 0, -sideZ + cornerRadius),
      new THREE.Vector3(sideX, 0, sideZ - cornerRadius)
    ));
    curvePath.add(new ArcCurve3(sideX - cornerRadius, sideZ - cornerRadius, cornerRadius, 0, Math.PI / 2));
    curvePath.add(new THREE.LineCurve3(
      new THREE.Vector3(sideX - cornerRadius, 0, sideZ),
      new THREE.Vector3(-sideX + cornerRadius, 0, sideZ)
    ));
    curvePath.add(new ArcCurve3(-sideX + cornerRadius, sideZ - cornerRadius, cornerRadius, Math.PI / 2, Math.PI));

    const relativeDetourZ = THREE.MathUtils.clamp(
      (detourZ ?? centerZ) - centerZ,
      -sideZ + detourOuterHalfSpan + cornerRadius,
      sideZ - detourOuterHalfSpan - cornerRadius
    );
    const topOuterZ = relativeDetourZ + detourOuterHalfSpan;
    const topInnerZ = relativeDetourZ + detourInnerHalfSpan;
    const bottomInnerZ = relativeDetourZ - detourInnerHalfSpan;
    const bottomOuterZ = relativeDetourZ - detourOuterHalfSpan;
    const detourX = -sideX - detourDepth;
    const tangentLength = Math.max((detourOuterHalfSpan - detourInnerHalfSpan) * 0.62, 0.75);

    curvePath.add(new THREE.LineCurve3(
      new THREE.Vector3(-sideX, 0, sideZ - cornerRadius),
      new THREE.Vector3(-sideX, 0, topOuterZ)
    ));

    if (detourDepth > 0) {
      curvePath.add(new THREE.CubicBezierCurve3(
        new THREE.Vector3(-sideX, 0, topOuterZ),
        new THREE.Vector3(-sideX, 0, topOuterZ - tangentLength),
        new THREE.Vector3(detourX, 0, topInnerZ + tangentLength),
        new THREE.Vector3(detourX, 0, topInnerZ)
      ));
      curvePath.add(new THREE.LineCurve3(
        new THREE.Vector3(detourX, 0, topInnerZ),
        new THREE.Vector3(detourX, 0, bottomInnerZ)
      ));
      curvePath.add(new THREE.CubicBezierCurve3(
        new THREE.Vector3(detourX, 0, bottomInnerZ),
        new THREE.Vector3(detourX, 0, bottomInnerZ - tangentLength),
        new THREE.Vector3(-sideX, 0, bottomOuterZ + tangentLength),
        new THREE.Vector3(-sideX, 0, bottomOuterZ)
      ));
    } else {
      curvePath.add(new THREE.LineCurve3(
        new THREE.Vector3(-sideX, 0, topOuterZ),
        new THREE.Vector3(-sideX, 0, bottomOuterZ)
      ));
    }

    curvePath.add(new THREE.LineCurve3(
      new THREE.Vector3(-sideX, 0, bottomOuterZ),
      new THREE.Vector3(-sideX, 0, -sideZ + cornerRadius)
    ));
    curvePath.add(new ArcCurve3(-sideX + cornerRadius, -sideZ + cornerRadius, cornerRadius, Math.PI, Math.PI * 1.5));

    return new THREE.ExtrudeGeometry(crossSection, {
      extrudePath: curvePath,
      steps: 48,
      closed: true,
      bevelEnabled: false
    });
  }, [
    sideX,
    sideZ,
    crossWidth,
    crossHeight,
    cornerRadius,
    centerZ,
    detourZ,
    detourDepth,
    detourOuterHalfSpan,
    detourInnerHalfSpan
  ]);

  return (
    <mesh
      position={[positionX, 13.0, centerZ]}
      geometry={geometry}
      renderOrder={6}
      raycast={DISABLE_RAYCAST}
    >
      <meshStandardMaterial
  color="#fde047"
  roughness={0.2}
  metalness={0.1}
  emissive="#ca8a04"
  emissiveIntensity={0.16}
  transparent
  opacity={0.54}
  depthWrite={false}
  depthTest
/>
    </mesh>
  );
};

const DuctConnector = ({
  startX,
  endX,
  positionZ,
  crossWidth,
  crossHeight
}) => {
  const geometry = useMemo(() => {
    const crossSection = new THREE.Shape();
    const cr = crossHeight / 2;
    const cx = Math.max(crossWidth / 2 - cr, 0.01);
    const joinOverlap = 0.06;

    crossSection.moveTo(-cx, -cr);
    crossSection.lineTo(cx, -cr);
    crossSection.absarc(cx, 0, cr, -Math.PI / 2, Math.PI / 2, false);
    crossSection.lineTo(-cx, cr);
    crossSection.absarc(-cx, 0, cr, Math.PI / 2, -Math.PI / 2, false);

    return new THREE.ExtrudeGeometry(crossSection, {
      extrudePath: new THREE.LineCurve3(
        new THREE.Vector3(startX - joinOverlap, 0, positionZ),
        new THREE.Vector3(endX + joinOverlap, 0, positionZ)
      ),
      steps: 1,
      bevelEnabled: false
    });
  }, [startX, endX, positionZ, crossWidth, crossHeight]);

  return (
    <mesh
      position={[0, 13.0, 0]}
      geometry={geometry}
      renderOrder={6}
      raycast={DISABLE_RAYCAST}
    >
      <meshStandardMaterial
  color="#fde047"
  roughness={0.2}
  metalness={0.1}
  emissive="#ca8a04"
  emissiveIntensity={0.16}
  transparent
  opacity={0.54}
  depthWrite={false}
  depthTest
/>
    </mesh>
  );
};

// ---------------------------------------------------------
// AUTOMATED SLIDING DOORS
// ---------------------------------------------------------
// ---------------------------------------------------------
// LEFT + RIGHT SIDE WALL SLIDING DOORS
// Both doors align with the widest front pathway at FRONT_Z
// ---------------------------------------------------------
const SideWallSlidingDoors = ({
  isTouring,
  doorHeight,
  leftDoorX,
  rightDoorX,
  doorCenterZ,
  doorWidthZ
}) => {
  const leftARef = useRef();
  const leftBRef = useRef();
  const rightARef = useRef();
  const rightBRef = useRef();

  const panelWidthZ = doorWidthZ / 2;

  useFrame((state, delta) => {
    const slideAmount = panelWidthZ * 1.05;

    const leftDoorTargets = [
      {
        ref: leftARef,
        closedZ: doorCenterZ - panelWidthZ / 2,
        openZ: doorCenterZ - panelWidthZ / 2 - slideAmount
      },
      {
        ref: leftBRef,
        closedZ: doorCenterZ + panelWidthZ / 2,
        openZ: doorCenterZ + panelWidthZ / 2 + slideAmount
      }
    ];

    const rightDoorTargets = [
      {
        ref: rightARef,
        closedZ: doorCenterZ - panelWidthZ / 2,
        openZ: doorCenterZ - panelWidthZ / 2 - slideAmount
      },
      {
        ref: rightBRef,
        closedZ: doorCenterZ + panelWidthZ / 2,
        openZ: doorCenterZ + panelWidthZ / 2 + slideAmount
      }
    ];

    [...leftDoorTargets, ...rightDoorTargets].forEach(({ ref, closedZ, openZ }) => {
      if (!ref.current) return;

      ref.current.position.z = THREE.MathUtils.lerp(
        ref.current.position.z,
        isTouring ? openZ : closedZ,
        3 * delta
      );
    });
  });

  const doorMaterial = (
    <meshStandardMaterial
      color="#38bdf8"
      transparent
      opacity={0.28}
      roughness={0.12}
      metalness={0.08}
      emissive="#0ea5e9"
      emissiveIntensity={0.16}
      depthWrite={false}
    />
  );

  return (
    <group>
      {/* LEFT SIDE DOOR */}
      <mesh ref={leftARef} position={[leftDoorX, doorHeight / 2, doorCenterZ - panelWidthZ / 2]}>
        <boxGeometry args={[0.24, doorHeight, panelWidthZ]} />
        {doorMaterial}
      </mesh>

      <mesh ref={leftBRef} position={[leftDoorX, doorHeight / 2, doorCenterZ + panelWidthZ / 2]}>
        <boxGeometry args={[0.24, doorHeight, panelWidthZ]} />
        {doorMaterial}
      </mesh>

      {/* RIGHT SIDE DOOR */}
      <mesh ref={rightARef} position={[rightDoorX, doorHeight / 2, doorCenterZ - panelWidthZ / 2]}>
        <boxGeometry args={[0.24, doorHeight, panelWidthZ]} />
        {doorMaterial}
      </mesh>

      <mesh ref={rightBRef} position={[rightDoorX, doorHeight / 2, doorCenterZ + panelWidthZ / 2]}>
        <boxGeometry args={[0.24, doorHeight, panelWidthZ]} />
        {doorMaterial}
      </mesh>
    </group>
  );
};


// ---------------------------------------------------------
// FRONT WALL SERVICE DOOR - FOR BOTTOM / FRONT PATHWAY
// ---------------------------------------------------------


// ---------------------------------------------------------
// SUBTLE BLUE NEON CORNER LIGHTS
// ---------------------------------------------------------

// ---------------------------------------------------------
// SUBTLE BLUE NEON CORNER LIGHTS - WITH REAL ROOM REFLECTION
// ---------------------------------------------------------
const NeonCornerLight = ({ x, z, xDir, zDir }) => {
  const neonMat = useMemo(
    () =>
      new THREE.MeshStandardMaterial({
        color: '#ccfbf1',
emissive: '#2dd4bf',
        emissiveIntensity: 2.2,
        roughness: 0.18,
        metalness: 0.18,
        transparent: true,
        opacity: 0.95
      }),
    []
  );

  const glowMat = useMemo(
    () =>
      new THREE.MeshBasicMaterial({
        color: '#22d3ee',
        transparent: true,
        opacity: 0.22,
        depthWrite: false
      }),
    []
  );

  const y = 13.45;

  return (
    <group position={[x, y, z]}>
      {/* Visible L-shaped neon tubes */}
      <mesh position={[xDir * 2.8, 0, 0]} material={neonMat}>
        <boxGeometry args={[5.6, 0.11, 0.11]} />
      </mesh>

      <mesh position={[0, 0, zDir * 2.8]} material={neonMat}>
        <boxGeometry args={[0.11, 0.11, 5.6]} />
      </mesh>

      {/* Blue glow panels around tubes */}
      <mesh position={[xDir * 2.8, -0.03, 0]} material={glowMat}>
        <boxGeometry args={[6.8, 0.05, 0.72]} />
      </mesh>

      <mesh position={[0, -0.03, zDir * 2.8]} material={glowMat}>
        <boxGeometry args={[0.72, 0.05, 6.8]} />
      </mesh>

    </group>
  );
};

// ---------------------------------------------------------
// ROOM-WIDE SUBTLE BLUE LIGHT WASH
// Helps neon reflect across walls, floor, racks and ducts
// ---------------------------------------------------------
const BlueRoomLightWash = ({ roomLeftX, roomRightX, backOuterWallZ, frontWallZ }) => {
  const centerX = (roomLeftX + roomRightX) / 2;
  const centerZ = (backOuterWallZ + frontWallZ) / 2;

  return (
    <group>
      {/* Very soft room-wide blue ambience */}
      <pointLight
  position={[centerX, 12.5, centerZ]}
  intensity={0.62}
  distance={105}
  decay={1.1}
  color="#2dd4bf"
/>

<pointLight
  position={[centerX, 13.2, frontWallZ - 5]}
  intensity={0.5}
  distance={85}
  decay={1.18}
  color="#22d3ee"
/>

<pointLight
  position={[centerX, 13.2, backOuterWallZ + 5]}
  intensity={0.46}
  distance={85}
  decay={1.18}
  color="#67e8f9"
/>

    </group>
  );
};

const InstancedRackPart = React.memo(({
  geometry,
  material,
  instances,
  interactive = false,
  renderOrder = 0,
  onPointerOver,
  onPointerMove,
  onPointerOut,
  onClick
}) => {
  const meshRef = useRef();
  const meshArgs = useMemo(
    () => [geometry, material, instances.length],
    [geometry, material, instances.length]
  );

  useLayoutEffect(() => {
    if (!meshRef.current) return;

    const transform = new THREE.Object3D();

    instances.forEach((instance, index) => {
      transform.position.set(...instance.position);
      transform.rotation.set(...(instance.rotation || [0, 0, 0]));
      transform.scale.set(...(instance.scale || [1, 1, 1]));
      transform.updateMatrix();
      meshRef.current.setMatrixAt(index, transform.matrix);
    });

    meshRef.current.instanceMatrix.setUsage(THREE.StaticDrawUsage);
    meshRef.current.instanceMatrix.needsUpdate = true;
    meshRef.current.computeBoundingSphere();
  }, [instances]);

  return (
    <instancedMesh
      ref={meshRef}
      args={meshArgs}
      matrixAutoUpdate={false}
      renderOrder={renderOrder}
      raycast={interactive ? undefined : DISABLE_RAYCAST}
      onPointerOver={onPointerOver}
      onPointerMove={onPointerMove}
      onPointerOut={onPointerOut}
      onClick={onClick}
    />
  );
});

// ---------------------------------------------------------
// OPTIMIZED OVER-RACK CEILING LIGHTS
// IMPORTANT PERFORMANCE FIX:
// Do NOT place pointLight inside every strip. Many dynamic lights cause
// shader recompiles/stutter. We keep strips/bulbs as cheap emissive meshes
// and use only ONE soft pointLight per SU zone.
// ---------------------------------------------------------
const OverRackNeonStrip = ({ position, length = 12, isDark }) => (
  <group position={position}>
    {/* slim visible ceiling strip - emissive only, no real light */}
    <mesh renderOrder={7} raycast={DISABLE_RAYCAST}>
      <boxGeometry args={[length, 0.1, 0.18]} />
      <meshBasicMaterial
        color={isDark ? '#d8fffb' : '#f4feff'}
        toneMapped={false}
      />
    </mesh>

    {/* cheap glow shell */}
    <mesh position={[0, -0.03, 0]} renderOrder={6} raycast={DISABLE_RAYCAST}>
      <boxGeometry args={[length * 0.92, 0.16, 0.46]} />
      <meshBasicMaterial
        color={isDark ? '#67e8f9' : '#67e8f9'}
        transparent
        opacity={isDark ? 0.3 : 0.18}
        depthWrite={false}
        toneMapped={false}
      />
    </mesh>
  </group>
);

const OverRackBulb = ({ position, isDark }) => (
  <group position={position}>
    <mesh renderOrder={7} raycast={DISABLE_RAYCAST}>
      <sphereGeometry args={[0.14, 10, 8]} />
      <meshBasicMaterial
        color={isDark ? '#f4fffe' : '#ffffff'}
        toneMapped={false}
      />
    </mesh>

    <mesh renderOrder={6} raycast={DISABLE_RAYCAST}>
      <sphereGeometry args={[0.3, 10, 8]} />
      <meshBasicMaterial
        color={isDark ? '#2dd4bf' : '#22d3ee'}
        transparent
        opacity={isDark ? 0.2 : 0.12}
        depthWrite={false}
        toneMapped={false}
      />
    </mesh>
  </group>
);

const OverRackLightWash = ({ position, isDark }) => (
  <pointLight
    position={position}
    intensity={isDark ? 1.62 : 0.92}
    distance={28}
    decay={1.55}
    color={isDark ? '#67e8f9' : '#22d3ee'}
  />
);

// ---------------------------------------------------------
// MAIN EXPORT
// ---------------------------------------------------------
function ScalableUnits({
  theme,
  isTouring,
  failedRackNums = [],
  rackStatusMap = {},
  onRackHover,
  onRackClick
}) {
  const isDark = theme === 'dark';

  // -------------------------------------------------------
  // ISOLATED ARCHITECTURE MATH
  // -------------------------------------------------------
  const roomLeftX = -50;
  const roomRightX = 50;
  const backOuterWallZ = -22;
  const backInnerWallZ = -16;
  const frontWallZ = 35;
  const doorHeight = 10.4;

  const MODEL_SCALE = 3.8;
  const RACK_VISUAL_BOOST = 1.0;

// Keep actual rack dimensions unchanged
const RACK_WIDTH_METERS = 0.6;
const RACK_DEPTH_METERS = 0.9;
const RACK_HEIGHT_METERS = 7 * 0.3048;

// Visual-only boost.
// This does NOT change the actual dimension constants above.
const RACK_HEIGHT_VISUAL_BOOST = 1.48;

const rackWidth = RACK_WIDTH_METERS * MODEL_SCALE;
const rackDepth = RACK_DEPTH_METERS * MODEL_SCALE;
const rackHeight = RACK_HEIGHT_METERS * MODEL_SCALE;

const visualRackWidth = rackWidth * RACK_VISUAL_BOOST;
const visualRackDepth = rackDepth * RACK_VISUAL_BOOST;
const visualRackHeight = rackHeight * RACK_HEIGHT_VISUAL_BOOST;

  const SU_CENTERS = [-24, -8, 8, 24];
  const LEFT_ROW_Z = [16, 12, 8, 0, -4, -8];
  const RIGHT_ROW_Z = [16, 12, 8, 4, 0, -4, -8];
  const colSpacingX = 3.6;

  const REAR_Z = -12;
const pathDepth = 6.0;
const branchWidth = 4.8;
const coldAisleXs = [-32, -16, 0, 16, 32];

// Move the front/widest pathway closer to where the racks end.
// Rack front edge = last rack row Z + half rack width.
// Small gap keeps the blue path from touching the rack model.
const RACK_TO_FRONT_PATH_GAP = 0.35;
const rackFrontEdgeZ =
  Math.max(...LEFT_ROW_Z, ...RIGHT_ROW_Z) + visualRackWidth / 2;

const FRONT_Z = rackFrontEdgeZ + RACK_TO_FRONT_PATH_GAP + pathDepth / 2;

// If your side door constants exist, keep them connected to FRONT_Z.
const SIDE_DOOR_CENTER_Z = FRONT_Z;
const SIDE_DOOR_WIDTH_Z = pathDepth;

  const PILLAR_Z = 4;
  const PILLAR_WIDTH = 1.6;
  const PILLAR_DEPTH = 1.8;
  const DUCT_Z_MIN = -9.5;
  const DUCT_Z_MAX = 17.5;
  const DUCT_FOOTPRINT_WIDTH = 11.3;
  const DUCT_CROSS_WIDTH = 3.0;
  const DUCT_CROSS_HEIGHT = 1.5;

  // -------------------------------------------------------
  // RACK GEOMETRY / MATERIAL OPTIMIZATION
  // -------------------------------------------------------
  const rackGeo = useMemo(
    () =>
      new THREE.BoxGeometry(
        visualRackDepth * 0.9,
        visualRackHeight * 0.86,
        visualRackWidth * 0.8
      ),
    [visualRackDepth, visualRackHeight, visualRackWidth]
  );

  const rackTopBottomGeo = useMemo(
    () => new THREE.BoxGeometry(visualRackDepth, 0.3, visualRackWidth),
    [visualRackDepth, visualRackWidth]
  );

  const rackSidePanelGeo = useMemo(
    () => new THREE.BoxGeometry(visualRackDepth * 0.94, visualRackHeight * 0.84, 0.16),
    [visualRackDepth, visualRackHeight]
  );

  const rackEndPostGeo = useMemo(
    () => new THREE.BoxGeometry(0.24, visualRackHeight, 0.24),
    [visualRackHeight]
  );

  const rackEndBeamGeo = useMemo(
    () => new THREE.BoxGeometry(0.24, 0.28, visualRackWidth),
    [visualRackWidth]
  );

  const rackBaseGeo = useMemo(
    () => new THREE.BoxGeometry(visualRackDepth, 0.42, visualRackWidth),
    [visualRackDepth, visualRackWidth]
  );

  const rackSideRibGeo = useMemo(
    () => new THREE.BoxGeometry(visualRackDepth * 0.78, 0.12, 0.055),
    [visualRackDepth]
  );

  const rackDoorSlabGeo = useMemo(
    () => new THREE.BoxGeometry(0.12, visualRackHeight * 0.82, visualRackWidth * 0.76),
    [visualRackHeight, visualRackWidth]
  );

  const hitBoxGeo = useMemo(
    () =>
      new THREE.BoxGeometry(
        visualRackDepth + 0.35,
        visualRackHeight + 1.0,
        visualRackWidth + 0.35
      ),
    [visualRackDepth, visualRackHeight, visualRackWidth]
  );

  const faceGeo = useMemo(
    () => new THREE.PlaneGeometry(visualRackWidth * 0.86, visualRackHeight * 0.86),
    [visualRackWidth, visualRackHeight]
  );

  const doorBarGeo = useMemo(
    () => new THREE.BoxGeometry(0.055, visualRackHeight * 0.94, 0.04),
    [visualRackHeight]
  );

  const doorHandleGeo = useMemo(
    () => new THREE.BoxGeometry(0.08, visualRackHeight * 0.28, 0.055),
    [visualRackHeight]
  );

  const serverFaceGeo = useMemo(
    () => new THREE.PlaneGeometry(visualRackWidth * 0.68, visualRackHeight * 0.7),
    [visualRackHeight, visualRackWidth]
  );

  const warningSignGeo = useMemo(
    () =>
      new THREE.PlaneGeometry(
        Math.min(visualRackWidth, visualRackDepth) * 0.78,
        Math.min(visualRackWidth, visualRackDepth) * 0.78
      ),
    [visualRackDepth, visualRackWidth]
  );

  const statusRailGeo = useMemo(
  () =>
    new THREE.BoxGeometry(
      0.10,
      visualRackHeight * 0.68,
      0.10
    ),
  [visualRackHeight]
);

  const serverFaceTexture = useMemo(() => {
    const canvas = document.createElement('canvas');
    canvas.width = 256;
    canvas.height = 512;
    const context = canvas.getContext('2d');

    context.fillStyle = '#121821';
    context.fillRect(0, 0, canvas.width, canvas.height);

    const slotCount = 20;
    const marginX = 14;
    const slotWidth = canvas.width - marginX * 2;
    const slotHeight = (canvas.height - 28) / slotCount;

    for (let slot = 0; slot < slotCount; slot++) {
      const y = 14 + slot * slotHeight;
      context.fillStyle = slot % 4 === 0 ? '#303a47' : '#252e39';
      context.fillRect(marginX, y + 2, slotWidth, slotHeight - 4);

      context.strokeStyle = '#64748b';
      context.globalAlpha = 0.62;
      context.lineWidth = 1.4;
      context.beginPath();
      context.moveTo(marginX, y + slotHeight - 2);
      context.lineTo(canvas.width - marginX, y + slotHeight - 2);
      context.stroke();

      context.strokeStyle = '#0b1017';
      context.globalAlpha = 0.9;
      context.lineWidth = 1;
      for (let vent = 0; vent < 7; vent++) {
        const ventX = marginX + 18 + vent * 18;
        context.beginPath();
        context.moveTo(ventX, y + 7);
        context.lineTo(ventX + 8, y + 7);
        context.stroke();
      }
    }

    context.globalAlpha = 1;
    const texture = new THREE.CanvasTexture(canvas);
    texture.colorSpace = THREE.SRGBColorSpace;
    texture.minFilter = THREE.LinearMipmapLinearFilter;
    texture.magFilter = THREE.LinearFilter;
    texture.generateMipmaps = true;
    texture.anisotropy = 4;
    texture.needsUpdate = true;
    return texture;
  }, []);

  const rearPerforatedTexture = useMemo(() => {
  const canvas = document.createElement('canvas');
  canvas.width = 512;
  canvas.height = 1024;

  const context = canvas.getContext('2d');

  // Teal/blue dark metal base
  const shellGradient = context.createLinearGradient(0, 0, canvas.width, canvas.height);
  shellGradient.addColorStop(0, '#12343d');
  shellGradient.addColorStop(0.45, '#071923');
  shellGradient.addColorStop(1, '#155e75');

  context.fillStyle = shellGradient;
  context.fillRect(0, 0, canvas.width, canvas.height);

  // Inner panel shade
  context.fillStyle = '#08131c';
  context.fillRect(28, 28, canvas.width - 56, canvas.height - 56);

  // Panel border
  context.strokeStyle = 'rgba(45, 212, 191, 0.32)';
  context.lineWidth = 2;
  context.strokeRect(36, 36, canvas.width - 72, canvas.height - 72);

  // Horizontal grill lines
  for (let y = 64; y < canvas.height - 64; y += 26) {
    context.strokeStyle =
      y % 130 === 0
        ? 'rgba(45, 212, 191, 0.28)'
        : 'rgba(103, 232, 249, 0.10)';

    context.lineWidth = y % 130 === 0 ? 1.6 : 0.8;
    context.beginPath();
    context.moveTo(54, y);
    context.lineTo(canvas.width - 54, y);
    context.stroke();
  }

  // Visible perforation rims + dark hole centers
  for (let y = 74; y < canvas.height - 74; y += 24) {
    const row = Math.round((y - 74) / 24);
    const offset = row % 2 === 0 ? 0 : 11;

    for (let x = 66 + offset; x < canvas.width - 66; x += 22) {
      // Soft teal glow around each perforation
      const glow = context.createRadialGradient(x, y, 1, x, y, 8);
      glow.addColorStop(0, 'rgba(45, 212, 191, 0.35)');
      glow.addColorStop(0.48, 'rgba(34, 211, 238, 0.16)');
      glow.addColorStop(1, 'rgba(2, 6, 23, 0)');
      context.fillStyle = glow;
      context.beginPath();
      context.arc(x, y, 8, 0, Math.PI * 2);
      context.fill();

      // Thin rim, so holes are readable from distance
      context.strokeStyle = 'rgba(204, 251, 241, 0.38)';
      context.lineWidth = 1.25;
      context.beginPath();
      context.arc(x, y, 5.2, 0, Math.PI * 2);
      context.stroke();

      // Dark hole center. The alpha map below cuts this visually.
      context.fillStyle = '#00040a';
      context.beginPath();
      context.arc(x, y, 4.65, 0, Math.PI * 2);
      context.fill();

      // Tiny highlight on rim
      context.fillStyle = 'rgba(236, 253, 245, 0.18)';
      context.beginPath();
      context.arc(x - 1.3, y - 1.3, 1.05, 0, Math.PI * 2);
      context.fill();
    }
  }

  // Subtle teal scan strips
  [132, 348, 566, 784].forEach((y, index) => {
    const gradient = context.createLinearGradient(80, y, canvas.width - 80, y);
    gradient.addColorStop(0, 'rgba(45, 212, 191, 0)');
    gradient.addColorStop(
      0.5,
      index % 2 === 0
        ? 'rgba(45, 212, 191, 0.28)'
        : 'rgba(34, 211, 238, 0.22)'
    );
    gradient.addColorStop(1, 'rgba(45, 212, 191, 0)');

    context.fillStyle = gradient;
    context.fillRect(80, y, canvas.width - 160, 3);
  });

  // Tiny status LEDs
  for (let y = 110; y < canvas.height - 100; y += 64) {
    context.fillStyle = y % 128 === 0 ? '#2dd4bf' : '#22d3ee';
    context.shadowColor = context.fillStyle;
    context.shadowBlur = 8;
    context.fillRect(canvas.width - 58, y, 5, 11);
    context.shadowBlur = 0;
  }

  // Edge teal glow
  const edgeGlow = context.createLinearGradient(0, 0, canvas.width, 0);
  edgeGlow.addColorStop(0, 'rgba(45, 212, 191, 0.32)');
  edgeGlow.addColorStop(0.15, 'rgba(45, 212, 191, 0)');
  edgeGlow.addColorStop(0.85, 'rgba(34, 211, 238, 0)');
  edgeGlow.addColorStop(1, 'rgba(34, 211, 238, 0.28)');
  context.fillStyle = edgeGlow;
  context.fillRect(0, 0, canvas.width, canvas.height);

  const texture = new THREE.CanvasTexture(canvas);
  texture.colorSpace = THREE.SRGBColorSpace;
  texture.minFilter = THREE.LinearMipmapLinearFilter;
  texture.magFilter = THREE.LinearFilter;
  texture.generateMipmaps = true;
  texture.anisotropy = 4;
  texture.needsUpdate = true;

  return texture;
}, []);

const rearPerforatedAlphaTexture = useMemo(() => {
  const canvas = document.createElement('canvas');
  canvas.width = 512;
  canvas.height = 1024;

  const context = canvas.getContext('2d');

  // White = visible metal panel
  context.fillStyle = '#ffffff';
  context.fillRect(0, 0, canvas.width, canvas.height);

  // Black = transparent / cut-through holes
  for (let y = 74; y < canvas.height - 74; y += 24) {
    const row = Math.round((y - 74) / 24);
    const offset = row % 2 === 0 ? 0 : 11;

    for (let x = 66 + offset; x < canvas.width - 66; x += 22) {
      context.fillStyle = '#000000';
      context.beginPath();
      context.arc(x, y, 5.15, 0, Math.PI * 2);
      context.fill();
    }
  }

  const texture = new THREE.CanvasTexture(canvas);
  texture.colorSpace = THREE.NoColorSpace;
  texture.minFilter = THREE.LinearMipmapLinearFilter;
  texture.magFilter = THREE.LinearFilter;
  texture.generateMipmaps = true;
  texture.anisotropy = 4;
  texture.needsUpdate = true;

  return texture;
}, []);

    const railGeo = useMemo(
    () => new THREE.BoxGeometry(0.065, visualRackHeight * 0.9, 0.055),
    [visualRackHeight]
  );

  const matRackShell = useMemo(
    () =>
      new THREE.MeshStandardMaterial({
        color: '#3f4c5c',
        emissive: '#082333',
        emissiveIntensity: 0.1,
        metalness: 0.74,
        roughness: 0.32
      }),
    []
  );

  const matRackSide = useMemo(
    () =>
      new THREE.MeshStandardMaterial({
        color: '#5d6b7a',
        emissive: '#0b2a39',
        emissiveIntensity: 0.1,
        metalness: 0.68,
        roughness: 0.36
      }),
    []
  );

  const matRackTop = useMemo(
    () =>
      new THREE.MeshStandardMaterial({
        color: '#758391',
        emissive: '#143040',
        emissiveIntensity: 0.1,
        metalness: 0.76,
        roughness: 0.3
      }),
    []
  );

  const matRackRecess = useMemo(
    () =>
      new THREE.MeshStandardMaterial({
        color: '#161c24',
        emissive: '#080b10',
        emissiveIntensity: 0.02,
        metalness: 0.48,
        roughness: 0.52
      }),
    []
  );

  const matFaceGlass = useMemo(
    () =>
      new THREE.MeshStandardMaterial({
        color: '#162534',
        emissive: '#031722',
        emissiveIntensity: 0.04,
        metalness: 0.38,
        roughness: 0.48
      }),
    []
  );

  const matFrame = useMemo(
    () =>
      new THREE.MeshStandardMaterial({
        color: '#748293',
        emissive: '#123040',
        emissiveIntensity: 0.06,
        metalness: 0.74,
        roughness: 0.3
      }),
    []
  );

  const matRackRib = useMemo(
    () =>
      new THREE.MeshStandardMaterial({
        color: '#26313d',
        emissive: '#06131c',
        emissiveIntensity: 0.04,
        metalness: 0.68,
        roughness: 0.4
      }),
    []
  );

  const matServerFace = useMemo(
    () =>
      new THREE.MeshStandardMaterial({
        map: serverFaceTexture,
        color: '#ffffff',
        emissive: '#0b1119',
        emissiveIntensity: 0.08,
        metalness: 0.42,
        roughness: 0.5
      }),
    [serverFaceTexture]
  );

  const matRearPerforated = useMemo(
    () =>
      new THREE.MeshStandardMaterial({
        map: rearPerforatedTexture,

        // Actual visible cut-through perforation holes
        alphaMap: rearPerforatedAlphaTexture,
        transparent: true,
        alphaTest: 0.48,

        color: '#dffdfa',
        emissive: '#0f766e',
        emissiveIntensity: 0.18,
        metalness: 0.52,
        roughness: 0.48,

        side: THREE.DoubleSide,
        depthWrite: true,
        depthTest: true
      }),
    [rearPerforatedTexture, rearPerforatedAlphaTexture]
  );

  const matRackLedPoints = useMemo(
    () =>
      new THREE.PointsMaterial({
        size: 0.105,
        sizeAttenuation: true,
        vertexColors: true,
        transparent: false,
        depthWrite: true,
        depthTest: true,
        toneMapped: false
      }),
    []
  );

  const warningSignTexture = useMemo(() => {
  const canvas = document.createElement('canvas');
  canvas.width = 512;
  canvas.height = 512;

  const context = canvas.getContext('2d');

  const drawTriangle = (strokeStyle, lineWidth, shadowBlur = 0) => {
    context.save();
    context.strokeStyle = strokeStyle;
    context.lineWidth = lineWidth;
    context.lineJoin = 'round';
    context.shadowColor = '#ef233c';
    context.shadowBlur = shadowBlur;

    context.beginPath();
    context.moveTo(256, 62);
    context.lineTo(448, 406);
    context.lineTo(64, 406);
    context.closePath();
    context.stroke();

    context.restore();
  };

  const drawExclamation = (strokeStyle, lineWidth, shadowBlur = 0) => {
    context.save();
    context.strokeStyle = strokeStyle;
    context.fillStyle = strokeStyle;
    context.lineWidth = lineWidth;
    context.lineCap = 'round';
    context.shadowColor = '#ef233c';
    context.shadowBlur = shadowBlur;

    context.beginPath();
    context.moveTo(256, 178);
    context.lineTo(256, 292);
    context.stroke();

    context.beginPath();
    context.arc(256, 348, lineWidth * 0.55, 0, Math.PI * 2);
    context.fill();

    context.restore();
  };

  context.clearRect(0, 0, canvas.width, canvas.height);

  context.fillStyle = 'rgba(127, 29, 29, 0.09)';
  context.beginPath();
  context.moveTo(256, 78);
  context.lineTo(429, 392);
  context.lineTo(83, 392);
  context.closePath();
  context.fill();

  drawTriangle('rgba(239, 35, 60, 0.28)', 34, 46);
  drawTriangle('rgba(255, 70, 86, 0.92)', 13, 20);
  drawTriangle('#ff8491', 4, 7);

  drawExclamation('rgba(239, 35, 60, 0.32)', 42, 44);
  drawExclamation('rgba(255, 84, 98, 0.94)', 20, 18);
  drawExclamation('#ffc1c7', 7, 5);

  const texture = new THREE.CanvasTexture(canvas);
  texture.colorSpace = THREE.SRGBColorSpace;
  texture.minFilter = THREE.LinearMipmapLinearFilter;
  texture.magFilter = THREE.LinearFilter;
  texture.generateMipmaps = true;
  texture.anisotropy = 4;
  texture.needsUpdate = true;

  return texture;
}, []);
  
  const matWarningSign = useMemo(
    () =>
      new THREE.MeshBasicMaterial({
        map: warningSignTexture,
        transparent: true,
        opacity: 0.82,
        depthWrite: false,
        depthTest: true,
        toneMapped: false,
        polygonOffset: true,
        polygonOffsetFactor: -2,
        polygonOffsetUnits: -2
      }),
    [warningSignTexture]
  );

  const matStatusGreen = useMemo(
  () =>
    new THREE.MeshBasicMaterial({
      color: '#22c55e',
      toneMapped: false,
    }),
  []
);

const matStatusAmber = useMemo(
  () =>
    new THREE.MeshBasicMaterial({
      color: '#f59e0b',
      toneMapped: false,
    }),
  []
);

const matStatusGrey = useMemo(
  () =>
    new THREE.MeshBasicMaterial({
      color: '#64748b',
      toneMapped: false,
    }),
  []
);

const matStatusRed = useMemo(
  () =>
    new THREE.MeshBasicMaterial({
      color: '#ef4444',
      toneMapped: false,
    }),
  []
);

  const hitBoxMaterial = useMemo(
    () =>
      new THREE.MeshBasicMaterial({
        transparent: true,
        opacity: 0,
        colorWrite: false,
        depthWrite: false,
        depthTest: false
      }),
    []
  );

  const rackLayouts = useMemo(() => {
    const layouts = [];
    const suCenters = [-24, -8, 8, 24];
    const leftRowZ = [16, 12, 8, 0, -4, -8];
    const rightRowZ = [16, 12, 8, 4, 0, -4, -8];

    suCenters.forEach((suBaseX, suIdx) => {
      for (let i = 1; i <= 13; i++) {
        const isLeftColumn = i <= 6;

        layouts.push({
          rackNum: suIdx * 13 + i,
          isLeftColumn,
          x: suBaseX + (isLeftColumn ? -colSpacingX : colSpacingX),
          y: visualRackHeight / 2,
          z: isLeftColumn ? leftRowZ[i - 1] : rightRowZ[i - 7]
        });
      }
    });

    return layouts;
  }, [colSpacingX, visualRackHeight]);

  const rackStatusRails = useMemo(() => {
  const rails = {
    green: [],
    amber: [],
    grey: [],
    red: [],
  };

  rackLayouts.forEach((layout) => {
    const status =
      rackStatusMap[layout.rackNum] || {};

    const percentage = Math.max(
      0,
      Math.min(
        100,
        Number(status.percentage) || 0
      )
    );

    let statusType = 'grey';

    if (status.hasDoaFailure) {
      statusType = 'red';
    } else if (percentage >= 100) {
      statusType = 'green';
    } else if (percentage > 0) {
      statusType = 'amber';
    }


    // Front face of rack
    const frontX = layout.isLeftColumn
      ? layout.x - visualRackDepth / 2 - 0.08
      : layout.x + visualRackDepth / 2 + 0.08;


    // Put one strip on each front edge.
    const edgeOffset =
      visualRackWidth * 0.42;


    rails[statusType].push({
      position: [
        frontX,
        layout.y + visualRackHeight * 0.04,
        layout.z - edgeOffset,
      ],
    });


    rails[statusType].push({
      position: [
        frontX,
        layout.y + visualRackHeight * 0.04,
        layout.z + edgeOffset,
      ],
    });
  });

  return rails;
}, [
  rackLayouts,
  rackStatusMap,
  visualRackDepth,
  visualRackHeight,
  visualRackWidth,
]);

  const failedRackSet = useMemo(
    () => new Set(failedRackNums.map((rackNum) => Number(rackNum))),
    [failedRackNums]
  );
  const hoveredRackRef = useRef(null);

  const rackFleet = useMemo(() => {
    const parts = {
      bodies: [],
      topCaps: [],
      bottomCaps: [],
      bases: [],
      sidePanels: [],
      frontRecesses: [],
      sideRibs: [],
      endPosts: [],
      endBeams: [],
      frontFaces: [],
      rearFaces: [],
      handles: [],
      doorBars: [],
      rails: [],
      serverFaces: [],
      warningSigns: [],
      hitBoxes: []
    };
    const ledPositions = [];
    const ledColors = [];
    const ledPalette = [
      new THREE.Color('#22c55e'),
      new THREE.Color('#38bdf8'),
      new THREE.Color('#f59e0b')
    ];

    const addPart = (collection, layout, localPosition = [0, 0, 0], rotation) => {
      collection.push({
        position: [
          layout.x + localPosition[0],
          layout.y + localPosition[1],
          layout.z + localPosition[2]
        ],
        rotation
      });
    };

    const topY = visualRackHeight / 2;
    const bottomY = -visualRackHeight / 2;
    const frontX = -visualRackDepth / 2 + 0.12;
    const backX = visualRackDepth / 2 - 0.12;

    rackLayouts.forEach((layout) => {
      const {
        isLeftColumn
      } = layout;
      const faceX = isLeftColumn
        ? -visualRackDepth / 2 + 0.11
        : visualRackDepth / 2 - 0.11;
      const rearX = isLeftColumn
        ? visualRackDepth / 2 - 0.03
        : -visualRackDepth / 2 + 0.03;
      const doorX = isLeftColumn
        ? -visualRackDepth / 2 + 0.015
        : visualRackDepth / 2 - 0.015;
      const faceRotY = isLeftColumn ? -Math.PI / 2 : Math.PI / 2;
      const rearRotY = isLeftColumn ? Math.PI / 2 : -Math.PI / 2;
      const depthDir = isLeftColumn ? -1 : 1;
      const outerFaceX = isLeftColumn
        ? -visualRackDepth / 2 + 0.22
        : visualRackDepth / 2 - 0.22;

      addPart(parts.bodies, layout);
      addPart(parts.topCaps, layout, [0, topY - 0.15, 0]);
      addPart(parts.bottomCaps, layout, [0, bottomY + 0.15, 0]);
      addPart(parts.bases, layout, [0, bottomY + 0.21, 0]);
      addPart(parts.sidePanels, layout, [0, 0, -visualRackWidth / 2 + 0.08]);
      addPart(parts.sidePanels, layout, [0, 0, visualRackWidth / 2 - 0.08]);
      addPart(parts.frontRecesses, layout, [outerFaceX, 0, 0]);

      [-0.29, 0, 0.29].forEach((heightRatio) => {
        [-1, 1].forEach((sideDirection) => {
          addPart(parts.sideRibs, layout, [
            0,
            visualRackHeight * heightRatio,
            sideDirection * (visualRackWidth / 2 - 0.028)
          ]);
        });
      });

      [frontX, backX].forEach((x) => {
        [-1, 1].forEach((zDirection) => {
          addPart(parts.endPosts, layout, [
            x,
            0,
            zDirection * (visualRackWidth / 2 - 0.12)
          ]);
        });

        [-1, 1].forEach((yDirection) => {
          addPart(parts.endBeams, layout, [
            x,
            yDirection * (visualRackHeight / 2 - 0.14),
            0
          ]);
        });
      });

      addPart(parts.frontFaces, layout, [faceX, 0.08, 0], [0, faceRotY, 0]);
      addPart(parts.rearFaces, layout, [rearX, 0.08, 0], [0, rearRotY, 0]);
      addPart(parts.handles, layout, [
        doorX - depthDir * 0.025,
        0.02,
        visualRackWidth * 0.42
      ]);

      [-1, 1].forEach((sideDirection) => {
        addPart(parts.doorBars, layout, [
          doorX - depthDir * 0.025,
          0.08,
          sideDirection * (visualRackWidth / 2 - 0.04)
        ]);
      });

      [-0.44, 0.44].forEach((zOffset) => {
        addPart(parts.rails, layout, [
          faceX + (isLeftColumn ? -0.018 : 0.018),
          0.08,
          zOffset * visualRackWidth
        ]);
      });

      addPart(
        parts.serverFaces,
        layout,
        [faceX + (isLeftColumn ? -0.082 : 0.082), 0, 0],
        [0, faceRotY, 0]
      );

      if (failedRackSet.has(layout.rackNum)) {
        const warningY = visualRackHeight * 0.06;
        const xFace = visualRackDepth / 2 + 0.045;
        const zFace = visualRackWidth / 2 + 0.045;

        addPart(parts.warningSigns, layout, [xFace, warningY, 0], [0, Math.PI / 2, 0]);
        addPart(parts.warningSigns, layout, [-xFace, warningY, 0], [0, -Math.PI / 2, 0]);
        addPart(parts.warningSigns, layout, [0, warningY, zFace], [0, 0, 0]);
        addPart(parts.warningSigns, layout, [0, warningY, -zFace], [0, Math.PI, 0]);
      }

      addPart(parts.hitBoxes, layout, [0, 0.5, 0]);

      const frontDirection = isLeftColumn ? -1 : 1;
      for (let row = 0; row < 13; row++) {
        const localY =
          -visualRackHeight * 0.32 +
          row * (visualRackHeight * 0.64 / 12);

        [0.29, 0.37].forEach((zRatio, column) => {
          const color = ledPalette[(row + column * 2) % ledPalette.length];
          ledPositions.push(
            layout.x + frontDirection * (visualRackDepth / 2 + 0.025),
            layout.y + localY,
            layout.z + visualRackWidth * zRatio
          );
          ledColors.push(color.r, color.g, color.b);
        });
      }
    });

    const ledGeometry = new THREE.BufferGeometry();
    ledGeometry.setAttribute(
      'position',
      new THREE.Float32BufferAttribute(ledPositions, 3)
    );
    ledGeometry.setAttribute(
      'color',
      new THREE.Float32BufferAttribute(ledColors, 3)
    );
    ledGeometry.computeBoundingSphere();

    return {
      ...parts,
      ledGeometry
    };
  }, [failedRackSet, rackLayouts, visualRackDepth, visualRackHeight, visualRackWidth]);

  const updateHoveredRackFromEvent = useCallback(
    (event) => {
      event.stopPropagation();
      const rackNum = rackLayouts[event.instanceId]?.rackNum;
      if (!rackNum || hoveredRackRef.current === rackNum) return;
      hoveredRackRef.current = rackNum;
      if (onRackHover) onRackHover(rackNum);
    },
    [onRackHover, rackLayouts]
  );

  const handleRackPointerOut = useCallback((event) => {
    event.stopPropagation();
    hoveredRackRef.current = null;
    if (onRackHover) onRackHover(null);
  }, [onRackHover]);

  const handleRackClick = useCallback(
    (event) => {
      event.stopPropagation();
      const rackNum = rackLayouts[event.instanceId]?.rackNum;
      if (rackNum && onRackClick) onRackClick(rackNum);
    },
    [onRackClick, rackLayouts]
  );

  const buildRackFleet = () => (
    <group>
      <InstancedRackPart geometry={rackGeo} material={matRackShell} instances={rackFleet.bodies} />
      <InstancedRackPart geometry={rackTopBottomGeo} material={matRackTop} instances={rackFleet.topCaps} />
      <InstancedRackPart geometry={rackTopBottomGeo} material={matRackShell} instances={rackFleet.bottomCaps} />
      <InstancedRackPart geometry={rackBaseGeo} material={matRackShell} instances={rackFleet.bases} />
      <InstancedRackPart geometry={rackSidePanelGeo} material={matRackSide} instances={rackFleet.sidePanels} />
      <InstancedRackPart geometry={rackDoorSlabGeo} material={matRackRecess} instances={rackFleet.frontRecesses} />
      <InstancedRackPart geometry={rackSideRibGeo} material={matRackRib} instances={rackFleet.sideRibs} />
      <InstancedRackPart geometry={rackEndPostGeo} material={matFrame} instances={rackFleet.endPosts} />
      <InstancedRackPart geometry={rackEndBeamGeo} material={matFrame} instances={rackFleet.endBeams} />
      <InstancedRackPart geometry={faceGeo} material={matFaceGlass} instances={rackFleet.frontFaces} />
      <InstancedRackPart geometry={faceGeo} material={matRearPerforated} instances={rackFleet.rearFaces} />
      <InstancedRackPart geometry={doorHandleGeo} material={matFrame} instances={rackFleet.handles} />
      <InstancedRackPart geometry={doorBarGeo} material={matFrame} instances={rackFleet.doorBars} />
      <InstancedRackPart geometry={railGeo} material={matFrame} instances={rackFleet.rails} />
      <InstancedRackPart geometry={serverFaceGeo} material={matServerFace} instances={rackFleet.serverFaces} />
      {rackFleet.warningSigns.length > 0 && (
        <InstancedRackPart
          geometry={warningSignGeo}
          material={matWarningSign}
          instances={rackFleet.warningSigns}
          renderOrder={5}
        />
      )}
      <InstancedRackPart
  geometry={statusRailGeo}
  material={matStatusGrey}
  instances={rackStatusRails.grey}
  renderOrder={6}
/>

<InstancedRackPart
  geometry={statusRailGeo}
  material={matStatusAmber}
  instances={rackStatusRails.amber}
  renderOrder={6}
/>

<InstancedRackPart
  geometry={statusRailGeo}
  material={matStatusGreen}
  instances={rackStatusRails.green}
  renderOrder={6}
/>

<InstancedRackPart
  geometry={statusRailGeo}
  material={matStatusRed}
  instances={rackStatusRails.red}
  renderOrder={6}
/>
      <points
        geometry={rackFleet.ledGeometry}
        material={matRackLedPoints}
        matrixAutoUpdate={false}
        raycast={DISABLE_RAYCAST}
      />
      <InstancedRackPart
        geometry={hitBoxGeo}
        material={hitBoxMaterial}
        instances={rackFleet.hitBoxes}
        interactive
        onPointerOver={updateHoveredRackFromEvent}
        onPointerMove={updateHoveredRackFromEvent}
        onPointerOut={handleRackPointerOut}
        onClick={handleRackClick}
      />
    </group>
  );

  const buildRoomArchitecture = () => {
  const wallColor = '#a7e8e2';
const wallMaterialProps = {
  color: wallColor,
    roughness: 0.64,
    metalness: 0.06,
    emissive: '#0e3a3b',
    emissiveIntensity: 0.035
  };
  const wallHeight = 14;
  const wallY = wallHeight / 2;
  const wallThickness = 1;
  const cornerOverlap = 0.8;

  // Doors now align with the widest/front pathway
  const doorCenterZ = SIDE_DOOR_CENTER_Z;
  const doorWidthZ = SIDE_DOOR_WIDTH_Z;
  const doorStartZ = doorCenterZ - doorWidthZ / 2;
  const doorEndZ = doorCenterZ + doorWidthZ / 2;

  const wallStartZ = backOuterWallZ - cornerOverlap;
  const wallEndZ = frontWallZ + cornerOverlap;

  // Wall segment before the door opening
  const backSegmentLength = doorStartZ - wallStartZ;
  const backSegmentCenterZ = wallStartZ + backSegmentLength / 2;

  // Wall segment after the door opening
  const frontSegmentLength = wallEndZ - doorEndZ;
  const frontSegmentCenterZ = doorEndZ + frontSegmentLength / 2;

  const frontBackWallWidth = roomRightX - roomLeftX + wallThickness * 2;

  return (
    <group>
      {/* LEFT WALL - back segment before door */}
      <mesh position={[roomLeftX, wallY, backSegmentCenterZ]}>
        <boxGeometry args={[wallThickness, wallHeight, backSegmentLength]} />
        <meshStandardMaterial {...wallMaterialProps} />
      </mesh>

      {/* LEFT WALL - front segment after door */}
      <mesh position={[roomLeftX, wallY, frontSegmentCenterZ]}>
        <boxGeometry args={[wallThickness, wallHeight, frontSegmentLength]} />
        <meshStandardMaterial {...wallMaterialProps} />
      </mesh>

      {/* RIGHT WALL - back segment before door */}
      <mesh position={[roomRightX, wallY, backSegmentCenterZ]}>
        <boxGeometry args={[wallThickness, wallHeight, backSegmentLength]} />
        <meshStandardMaterial {...wallMaterialProps} />
      </mesh>

      {/* RIGHT WALL - front segment after door */}
      <mesh position={[roomRightX, wallY, frontSegmentCenterZ]}>
        <boxGeometry args={[wallThickness, wallHeight, frontSegmentLength]} />
        <meshStandardMaterial {...wallMaterialProps} />
      </mesh>

      {/* LEFT DOOR HEADER */}
      <mesh
        position={[
          roomLeftX,
          doorHeight + (wallHeight - doorHeight) / 2,
          doorCenterZ
        ]}
      >
        <boxGeometry
          args={[
            wallThickness,
            wallHeight - doorHeight,
            doorWidthZ
          ]}
        />
        <meshStandardMaterial {...wallMaterialProps} />
      </mesh>

      {/* RIGHT DOOR HEADER */}
      <mesh
        position={[
          roomRightX,
          doorHeight + (wallHeight - doorHeight) / 2,
          doorCenterZ
        ]}
      >
        <boxGeometry
          args={[
            wallThickness,
            wallHeight - doorHeight,
            doorWidthZ
          ]}
        />
        <meshStandardMaterial {...wallMaterialProps} />
      </mesh>

      {/* BOTH LEFT + RIGHT SIDE DOORS */}
      <SideWallSlidingDoors
        isTouring={isTouring}
        doorHeight={doorHeight}
        leftDoorX={roomLeftX}
        rightDoorX={roomRightX}
        doorCenterZ={doorCenterZ}
        doorWidthZ={doorWidthZ}
      />

      {/* BACK OUTER WALL */}
      <mesh position={[0, wallY, backOuterWallZ]}>
        <boxGeometry args={[frontBackWallWidth, wallHeight, wallThickness]} />
        <meshStandardMaterial {...wallMaterialProps} />
      </mesh>

      {/* BACK INNER WALL / COOLER PARTITION */}
      <mesh position={[0, wallY, backInnerWallZ]}>
        <boxGeometry args={[frontBackWallWidth, wallHeight, wallThickness]} />
        <meshStandardMaterial
          {...wallMaterialProps}
          transparent
          opacity={0.64}
        />
      </mesh>

      {/* FRONT WALL - SOLID, NO DOOR HERE */}
      <mesh position={[0, wallY, frontWallZ]}>
        <boxGeometry args={[frontBackWallWidth, wallHeight, wallThickness]} />
        <meshStandardMaterial {...wallMaterialProps} />
      </mesh>

      {/* CYAN WALL LIGHT STRIPS */}
      {[-32, -16, 0, 16, 32].map((x, i) => (
        <mesh key={`wall-wash-back-${i}`} position={[x, 12.65, backOuterWallZ + 0.54]}>
          <boxGeometry args={[7.2, 0.12, 0.08]} />
          <meshBasicMaterial color="#67e8f9" toneMapped={false} />
        </mesh>
      ))}

      {[-13, 13].map((z, i) => (
        <React.Fragment key={`wall-wash-side-${i}`}>
          <mesh position={[roomLeftX + 0.54, 12.35, z]}>
            <boxGeometry args={[0.08, 0.1, 8.5]} />
            <meshBasicMaterial color="#22d3ee" toneMapped={false} />
          </mesh>
          <mesh position={[roomRightX - 0.54, 12.35, z]}>
            <boxGeometry args={[0.08, 0.1, 8.5]} />
            <meshBasicMaterial color="#22d3ee" toneMapped={false} />
          </mesh>
        </React.Fragment>
      ))}

      {/* BLUE COOLERS */}
      {/* MILD BLUE COOLERS */}
{Array.from({ length: 11 }).map((_, i) => (
  <mesh key={`util-${i}`} position={[-34.5 + i * 6.9, 3.5, -19]}>
    <boxGeometry args={[4, 7, 3]} />
    <meshStandardMaterial
      color={isDark ? '#2563eb' : '#60a5fa'}
      roughness={0.5}
      metalness={0.08}
      emissive={isDark ? '#1e3a8a' : '#dbeafe'}
      emissiveIntensity={isDark ? 0.08 : 0.03}
    />
  </mesh>
))}

      {/* BLUE NEON CORNER LIGHTS */}
      <NeonCornerLight
        x={roomLeftX + 1.2}
        z={backOuterWallZ + 1.2}
        xDir={1}
        zDir={1}
      />

      <NeonCornerLight
        x={roomRightX - 1.2}
        z={backOuterWallZ + 1.2}
        xDir={-1}
        zDir={1}
      />

      <NeonCornerLight
        x={roomLeftX + 1.2}
        z={frontWallZ - 1.2}
        xDir={1}
        zDir={-1}
      />

      <NeonCornerLight
        x={roomRightX - 1.2}
        z={frontWallZ - 1.2}
        xDir={-1}
        zDir={-1}
      />

      <BlueRoomLightWash
        roomLeftX={roomLeftX}
        roomRightX={roomRightX}
        backOuterWallZ={backOuterWallZ}
        frontWallZ={frontWallZ}
      />
    </group>
  );
};

  const buildWalkway = () => {
  const walkY = 0.02;
  const walkColor = '#93c5fd';
  const tapeColor = '#eab308';
  const tapeWidth = 0.3;
  const slabThickness = 0.035;
  const tapeThickness = 0.008;
  const tapeY = walkY + slabThickness / 2 + tapeThickness / 2 + 0.004;

  const elements = [];

  const horizontalMinX = coldAisleXs[0] - branchWidth / 2;
  const horizontalMaxX = coldAisleXs[coldAisleXs.length - 1] + branchWidth / 2;
  const horizontalCenterX = (horizontalMinX + horizontalMaxX) / 2;
  const horizontalWidth = horizontalMaxX - horizontalMinX;

  // LEFT SIDE DOOR CONNECTOR PATH
  const leftConnectorStartX = roomLeftX;
  const leftConnectorEndX = horizontalMinX;
  const leftConnectorCenterX = (leftConnectorStartX + leftConnectorEndX) / 2;
  const leftConnectorWidth = Math.abs(leftConnectorEndX - leftConnectorStartX);

  elements.push(
    <mesh key="w-left-door-path" position={[leftConnectorCenterX, walkY, FRONT_Z]}>
      <boxGeometry args={[leftConnectorWidth, slabThickness, pathDepth]} />
      <meshStandardMaterial color={walkColor} roughness={0.7} />
    </mesh>,

    <mesh
      key="w-left-door-path-top"
      position={[
        leftConnectorCenterX,
        tapeY,
        FRONT_Z - pathDepth / 2 + tapeWidth / 2
      ]}
    >
      <boxGeometry args={[leftConnectorWidth, tapeThickness, tapeWidth]} />
      <meshBasicMaterial color={tapeColor} />
    </mesh>,

    <mesh
      key="w-left-door-path-bottom"
      position={[
        leftConnectorCenterX,
        tapeY,
        FRONT_Z + pathDepth / 2 - tapeWidth / 2
      ]}
    >
      <boxGeometry args={[leftConnectorWidth, tapeThickness, tapeWidth]} />
      <meshBasicMaterial color={tapeColor} />
    </mesh>
  );

  // RIGHT SIDE DOOR CONNECTOR PATH
  const rightConnectorStartX = horizontalMaxX;
  const rightConnectorEndX = roomRightX;
  const rightConnectorCenterX = (rightConnectorStartX + rightConnectorEndX) / 2;
  const rightConnectorWidth = Math.abs(rightConnectorEndX - rightConnectorStartX);

  elements.push(
    <mesh key="w-right-door-path" position={[rightConnectorCenterX, walkY, FRONT_Z]}>
      <boxGeometry args={[rightConnectorWidth, slabThickness, pathDepth]} />
      <meshStandardMaterial color={walkColor} roughness={0.7} />
    </mesh>,

    <mesh
      key="w-right-door-path-top"
      position={[
        rightConnectorCenterX,
        tapeY,
        FRONT_Z - pathDepth / 2 + tapeWidth / 2
      ]}
    >
      <boxGeometry args={[rightConnectorWidth, tapeThickness, tapeWidth]} />
      <meshBasicMaterial color={tapeColor} />
    </mesh>,

    <mesh
      key="w-right-door-path-bottom"
      position={[
        rightConnectorCenterX,
        tapeY,
        FRONT_Z + pathDepth / 2 - tapeWidth / 2
      ]}
    >
      <boxGeometry args={[rightConnectorWidth, tapeThickness, tapeWidth]} />
      <meshBasicMaterial color={tapeColor} />
    </mesh>
  );

  // FRONT HORIZONTAL CORRIDOR
  elements.push(
    <mesh key="w-front" position={[horizontalCenterX, walkY, FRONT_Z]}>
      <boxGeometry args={[horizontalWidth, slabThickness, pathDepth]} />
      <meshStandardMaterial color={walkColor} roughness={0.7} />
    </mesh>,

    <mesh key="w-front-t" position={[horizontalCenterX, tapeY, FRONT_Z - pathDepth / 2 + tapeWidth / 2]}>
      <boxGeometry args={[horizontalWidth, tapeThickness, tapeWidth]} />
      <meshBasicMaterial color={tapeColor} />
    </mesh>,

    <mesh key="w-front-b" position={[horizontalCenterX, tapeY, FRONT_Z + pathDepth / 2 - tapeWidth / 2]}>
      <boxGeometry args={[horizontalWidth, tapeThickness, tapeWidth]} />
      <meshBasicMaterial color={tapeColor} />
    </mesh>
  );

  // REAR HORIZONTAL CORRIDOR
  elements.push(
    <mesh key="w-rear" position={[horizontalCenterX, walkY, REAR_Z]}>
      <boxGeometry args={[horizontalWidth, slabThickness, pathDepth]} />
      <meshStandardMaterial color={walkColor} roughness={0.7} />
    </mesh>,

    <mesh key="w-rear-t" position={[horizontalCenterX, tapeY, REAR_Z - pathDepth / 2 + tapeWidth / 2]}>
      <boxGeometry args={[horizontalWidth, tapeThickness, tapeWidth]} />
      <meshBasicMaterial color={tapeColor} />
    </mesh>,

    <mesh key="w-rear-b" position={[horizontalCenterX, tapeY, REAR_Z + pathDepth / 2 - tapeWidth / 2]}>
      <boxGeometry args={[horizontalWidth, tapeThickness, tapeWidth]} />
      <meshBasicMaterial color={tapeColor} />
    </mesh>
  );

  // VERTICAL BRANCHES
  const verticalZStart = REAR_Z + pathDepth / 2;
  const verticalZEnd = FRONT_Z - pathDepth / 2;
  const verticalLength = verticalZEnd - verticalZStart;
  const verticalCenterZ = verticalZStart + verticalLength / 2;

  coldAisleXs.forEach((bx, i) => {
    elements.push(
      <mesh key={`b-${i}`} position={[bx, walkY, verticalCenterZ]}>
        <boxGeometry args={[branchWidth, slabThickness, verticalLength]} />
        <meshStandardMaterial color={walkColor} roughness={0.7} />
      </mesh>,

      <mesh key={`b-${i}-l`} position={[bx - branchWidth / 2 + tapeWidth / 2, tapeY, verticalCenterZ]}>
        <boxGeometry args={[tapeWidth, tapeThickness, verticalLength]} />
        <meshBasicMaterial color={tapeColor} />
      </mesh>,

      <mesh key={`b-${i}-r`} position={[bx + branchWidth / 2 - tapeWidth / 2, tapeY, verticalCenterZ]}>
        <boxGeometry args={[tapeWidth, tapeThickness, verticalLength]} />
        <meshBasicMaterial color={tapeColor} />
      </mesh>
    );
  });

  return elements;
};

  const buildSULayout = () => {
    const elements = [];

    const ductZCenter = (DUCT_Z_MIN + DUCT_Z_MAX) / 2;
    const ductLength = DUCT_Z_MAX - DUCT_Z_MIN;
    const ductSideX = DUCT_FOOTPRINT_WIDTH / 2 - DUCT_CROSS_WIDTH / 2;
    const ductSideZ = ductLength / 2 - DUCT_CROSS_WIDTH / 2;
    const ductCornerRadius = DUCT_CROSS_WIDTH / 2;
    const connectorInsetX = ductSideX - ductCornerRadius;

    for (let suIdx = 0; suIdx < SU_CENTERS.length - 1; suIdx++) {
      const connectorStartX = SU_CENTERS[suIdx] + connectorInsetX;
      const connectorEndX = SU_CENTERS[suIdx + 1] - connectorInsetX;

      elements.push(
        <DuctConnector
          key={`duct-connector-back-${suIdx}`}
          startX={connectorStartX}
          endX={connectorEndX}
          positionZ={ductZCenter - ductSideZ}
          crossWidth={DUCT_CROSS_WIDTH}
          crossHeight={DUCT_CROSS_HEIGHT}
        />,
        <DuctConnector
          key={`duct-connector-front-${suIdx}`}
          startX={connectorStartX}
          endX={connectorEndX}
          positionZ={ductZCenter + ductSideZ}
          crossWidth={DUCT_CROSS_WIDTH}
          crossHeight={DUCT_CROSS_HEIGHT}
        />
      );
    }

    for (let suIdx = 0; suIdx < 4; suIdx++) {
      const suNum = suIdx + 1;
      const suBaseX = SU_CENTERS[suIdx];
      const pillarX = suBaseX - colSpacingX;

      elements.push(
        <FiberDuct
          key={`duct-${suNum}`}
          positionX={suBaseX}
          centerZ={ductZCenter}
          lengthZ={ductLength}
          footprintWidthX={DUCT_FOOTPRINT_WIDTH}
          crossWidth={DUCT_CROSS_WIDTH}
          crossHeight={DUCT_CROSS_HEIGHT}
          detourZ={PILLAR_Z}
          detourDepth={2.4}
          detourOuterHalfSpan={4.4}
          detourInnerHalfSpan={2.8}
        />
      );

      // ---------------------------------------------------------
      // OVER-RACK CEILING LIGHTS
      // Positioned below the ducts and above the racks so the rack tops
      // and perforated rear panels catch teal/cyan reflections.
      // ---------------------------------------------------------
      elements.push(
        <OverRackNeonStrip
          key={`over-rack-strip-front-${suNum}`}
          position={[suBaseX, 12.25, 13.2]}
          length={12.4}
          isDark={isDark}
        />,
        <OverRackNeonStrip
          key={`over-rack-strip-mid-${suNum}`}
          position={[suBaseX, 12.25, 4.0]}
          length={12.4}
          isDark={isDark}
        />,
        <OverRackNeonStrip
          key={`over-rack-strip-rear-${suNum}`}
          position={[suBaseX, 12.25, -5.2]}
          length={12.4}
          isDark={isDark}
        />,
        <OverRackBulb
          key={`over-rack-bulb-fl-${suNum}`}
          position={[suBaseX - 4.25, 12.08, 9.2]}
          isDark={isDark}
        />,
        <OverRackBulb
          key={`over-rack-bulb-fr-${suNum}`}
          position={[suBaseX + 4.25, 12.08, 9.2]}
          isDark={isDark}
        />,
        <OverRackBulb
          key={`over-rack-bulb-bl-${suNum}`}
          position={[suBaseX - 4.25, 12.08, -2.8]}
          isDark={isDark}
        />,
        <OverRackBulb
          key={`over-rack-bulb-br-${suNum}`}
          position={[suBaseX + 4.25, 12.08, -2.8]}
          isDark={isDark}
        />,
        <OverRackLightWash
          key={`over-rack-light-wash-${suNum}`}
          position={[suBaseX, 11.5, 4.0]}
          isDark={isDark}
        />
      );

      elements.push(
        <group key={`pillar-${suNum}`} position={[pillarX, 0, PILLAR_Z]}>
          <mesh position={[0, 7, 0]}>
            <boxGeometry args={[PILLAR_WIDTH, 14, PILLAR_DEPTH]} />
            <meshStandardMaterial
              color="#6b7280"
              roughness={0.82}
              metalness={0.06}
            />
          </mesh>
          <mesh position={[0, 0.12, 0]}>
            <boxGeometry args={[PILLAR_WIDTH + 0.28, 0.24, PILLAR_DEPTH + 0.28]} />
            <meshStandardMaterial color="#4b5563" roughness={0.68} metalness={0.16} />
          </mesh>
        </group>
      );

      for (let i = 1; i <= 13; i++) {
        const rackNum = suIdx * 13 + i;
        const isLeftColumn = i <= 6;

        const globalX = suBaseX + (isLeftColumn ? -colSpacingX : colSpacingX);
        const globalZ = isLeftColumn
          ? LEFT_ROW_Z[i - 1]
          : RIGHT_ROW_Z[i - 7];

        const floorNumberX = isLeftColumn
          ? -visualRackDepth / 2 - 0.55
          : visualRackDepth / 2 + 0.55;

        const textColor = isDark ? '#ffffff' : '#000000';

        elements.push(
          <Text
            key={`floor-number-${rackNum}`}
            raycast={DISABLE_RAYCAST}
            position={[globalX + floorNumberX, 0.08, globalZ]}
            rotation={[-Math.PI / 2, 0, 0]}
            fontSize={0.72}
            color={textColor}
            fontWeight={900}
            anchorX="center"
            anchorY="middle"
          >
            {rackNum}
          </Text>
        );
      }
    }

    return elements;
  };

  return (
    <group>
      {buildRoomArchitecture()}
      {buildWalkway()}
      {buildSULayout()}
      {buildRackFleet()}
    </group>
  );
}

export default React.memo(ScalableUnits);